import React, { useEffect, useState } from 'react'
import OtpInput from 'react-otp-input'

import getLastCharacters from '@/libs/helpers/get-last-characters'
import { useAppSelector } from '@/libs/hooks/use-app-selector'

type TOTPInput = {
  readonly onChange: (otp: string) => void
  readonly isError?: boolean
  readonly errorText?: string
  readonly traceId?: string
  readonly numInputs?: number
}

export default function OTPInput({
  onChange,
  isError,
  errorText,
  traceId,
  numInputs = 6,
}: TOTPInput) {
  const [otp, setOtp] = useState('')
  const { partnerId } = useAppSelector((state) => state.user)

  const handleChange = (value: string) => {
    // For both OTP and password input, ensure we only accept numeric values
    const numericValue = value.replace(/\D/g, '')

    // Limit length based on input type
    const newValue = partnerId
      ? numericValue.slice(0, numInputs) // For password input, limit to numInputs length
      : numericValue

    setOtp(newValue)
    onChange(newValue)
  }

  useEffect(() => {
    document.body.classList.add('overflow-hidden')
    return () => {
      document.body.classList.remove('overflow-hidden')
    }
  }, [])

  if (partnerId) {
    // Render single password-masked number input
    return (
      <div
        className='flex w-full flex-col items-center justify-center gap-2'
        data-testid='password-input'
      >
        <input
          type='password'
          inputMode='numeric'
          pattern='[0-9]*'
          value={otp}
          onChange={(e) => handleChange(e.target.value)}
          maxLength={numInputs}
          className={`sentry-mask !h-12 w-full max-w-[300px] rounded-lg border ${
            isError ? 'border-pop-error-outline' : 'border-pop-gray-5'
          } text-center focus:border-blue-500 focus:outline-none`}
          autoComplete='one-time-code'
        />
        <div className='col-span-6 max-w-64'>
          {isError && errorText && (
            <span className='text-left text-xs text-pop-error'>
              {errorText} {traceId && `(${getLastCharacters(traceId)})`}
            </span>
          )}
        </div>
      </div>
    )
  }

  // Original OTP input
  return (
    <div className='flex w-full flex-col items-center justify-center gap-2' data-testid='otp-input'>
      <OtpInput
        value={otp}
        onChange={handleChange}
        numInputs={numInputs}
        containerStyle='flex gap-2 w-full justify-center items-center'
        renderInput={(props) => (
          <input
            {...props}
            type='password'
            inputMode='numeric'
            pattern='[0-9]*'
            className={`sentry-mask !size-[46px] rounded-lg border md:h-12 md:w-12 ${
              isError ? 'border-pop-error-outline' : 'border-pop-gray-5'
            } text-center focus:border-blue-500 focus:outline-none`}
          />
        )}
      />
      <div className='col-span-6 max-w-64'>
        {isError && errorText && (
          <span className='text-left text-xs text-pop-error'>
            {errorText} {traceId && `(${getLastCharacters(traceId)})`}
          </span>
        )}
      </div>
    </div>
  )
}
